import {
	type AsyncStorage,
	createIndexedDBStorageProvider,
} from '@atlassian/jira-browser-storage-providers';
import { setMark } from '@atlassian/jira-common-performance';

import { ff } from '@atlassian/jira-feature-flagging';

import type { MobileRestApiResponse } from '@atlassian/jira-software-board-uif-types';
import { getAccountId, isValid, preferences } from '../common';
import type { BoardQueryParameters } from '../utils/types';

export const uifBoardCache: AsyncStorage = createIndexedDBStorageProvider('cmp-board');

export interface BoardCachedEntry {
	timestamp: number;
	// This is going to be obsolete.
	etag: string;
	data: MobileRestApiResponse;
}

const isJSWUIFBoardCacheEnabled = () => ff('jsw.uif.board-indexeddb-killswitch');

export function buildBoardCachedEntryKey({
	boardId,
	accountId,
	queryParameters,
}: {
	boardId: number | string;
	accountId: string;
	queryParameters?: BoardQueryParameters;
}): string[] {
	const params: (string | number)[] = [accountId, boardId];

	if (queryParameters) {
		params.push(JSON.stringify(queryParameters));
	}

	const paramString = params.join('::');
	return [`local-device-cache::${paramString}`, `board-etag::${paramString}`];
}

interface BoardCachedEntryParams {
	boardId: number | string;
	queryParameters?: BoardQueryParameters;
}

export async function setBoardCachedEntry(
	{ boardId, queryParameters }: BoardCachedEntryParams,
	entry: BoardCachedEntry,
): Promise<void> {
	// If both features are disabled, do nothing (handles holdout group)
	if (!ff('jsw.perf.local-device-cache_bqeml') && !isJSWUIFBoardCacheEnabled()) {
		return Promise.resolve();
	}

	if (ff('jsw.perf.local-device-cache_bqeml')) {
		if (!entry.data.localDeviceCacheEnabled) {
			uifBoardCache.clearAll();
			return Promise.resolve();
		}
	} else {
		// Handle old labs preference
		const isCachePreferenceEnabled = await preferences.get('user.indexeddb.cache.enabled');
		if (!isCachePreferenceEnabled) return Promise.resolve();
	}

	const accountId = getAccountId() ?? 'no-account-id';
	const [key, oldKey] = buildBoardCachedEntryKey({ boardId, accountId, queryParameters });

	// When new switch is enabled, clear old cache key and save new one
	if (ff('jsw.perf.local-device-cache_bqeml')) {
		uifBoardCache.remove(oldKey);
		return uifBoardCache.set(key, entry);
	}

	return uifBoardCache.set(oldKey, entry);
}

export async function getBoardCachedEntry({
	boardId,
	queryParameters,
}: BoardCachedEntryParams): Promise<BoardCachedEntry | null> {
	if (!ff('jsw.perf.local-device-cache_bqeml') && !isJSWUIFBoardCacheEnabled()) {
		return null;
	}

	const accountId = getAccountId() ?? 'no-account-id';
	const [key, oldKey] = buildBoardCachedEntryKey({ boardId, accountId, queryParameters });
	const cacheKey = ff('jsw.perf.local-device-cache_bqeml') ? key : oldKey;

	try {
		setMark('uif-board-fetch-read-cache-start');
		const entry = await uifBoardCache.get(cacheKey);
		if (entry) {
			setMark('uif-board-fetch-read-cache-end');
		} else {
			return null;
		}
		if (!isValid(entry)) {
			await uifBoardCache.remove(cacheKey);
			return null;
		}
		return entry;
	} catch (err) {
		return null;
	}
}
